import axios from "axios"

const api = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  },
})

export interface CallCost {
  product_costs: Array<{
    product_name: string
    unit_price: number
    cost: number
  }>
  total_duration_seconds: number
  total_duration_unit_price: number
  total_one_time_price: number
  combined_cost: number
}

// Update the Call interface to include public_log_url
export interface Call {
  call_id: string
  call_type: "web_call" | "phone_call"
  agent_id: string
  call_status: "registered" | "ongoing" | "ended" | "error"
  start_timestamp: number | string
  end_timestamp: number | string
  disconnection_reason: string
  call_cost: CallCost
  transcript?: string
  recording_url?: string
  public_log_url?: string
  call_analysis?: {
    sentiment: "Positive" | "Negative" | "Neutral"
    call_successful?: boolean
  }
  from_number?: string
  to_number?: string
  duration_seconds?: number
}

export interface ListCallsResponse {
  calls: Call[]
}

export interface CreateWebCallResponse {
  call_type: "web_call"
  access_token: string
  call_id: string
  agent_id: string
  call_status: "registered" | "ongoing" | "ended" | "error"
  // ... (other fields)
}

// Add this interface for the createPhoneCall response
export interface CreatePhoneCallResponse {
  call_type: "phone_call"
  call_id: string
  agent_id: string
  call_status: "registered" | "ongoing" | "ended" | "error"
  from_number: string
  to_number: string
}

/** Body for Retell `POST /v2/register-phone-call` (custom telephony / dial-to-SIP). */
export type RegisterPhoneCallParams = Record<string, unknown> & {
  agent_id: string
}

class CallService {
  // Update the listCalls method in callService.ts to handle the userId parameter better
  async listCalls(options: any = {}, userId?: string): Promise<Call[]> {
    try {
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      }

      // Add userId to headers if available
      if (userId) {
        headers["user-id"] = userId
      }

      const response = await fetch("/api/calls/list", {
        method: "POST",
        headers,
        body: JSON.stringify(options),
      })

      if (!response.ok) {
        throw new Error(`Error: ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error("Error listing calls:", error)
      return []
    }
  }

  calculateCallCost(call: Call): number {
    // Calculate the cost of a single call
    // Base rate: $0.03 per minute (3 cents)
    const durationMinutes = this.calculateDurationMinutes(call)
    return Math.ceil(durationMinutes * 3) // Cost in cents
  }

  calculateDurationMinutes(call: Call): number {
    // Calculate duration in minutes
    if (call.duration_seconds) {
      return call.duration_seconds / 60
    }

    // If duration_seconds is not available, calculate from timestamps
    const startTime = new Date(call.start_timestamp).getTime()
    const endTime = new Date(call.end_timestamp).getTime()
    const durationMs = endTime - startTime
    return durationMs / (1000 * 60) // Convert ms to minutes
  }

  calculateTotalCost(calls: Call[]): number {
    // Calculate the total cost of all calls
    return calls.reduce((total, call) => total + this.calculateCallCost(call), 0)
  }

  createWebCall = async (agentId: string, options?: any) => {
    console.log("Call service createWebCall with options:", options)
    const requestData = {
      agent_id: agentId,
      ...options,
    }
    console.log("Request data being sent to API:", requestData)
    const response = await api.post<CreateWebCallResponse>("/calls/web", requestData)
    return response.data
  }

  // Add this method to the callService object before the closing brace
  createPhoneCall = async (agentId: string, fromNumber: string, toNumber: string, options?: any) => {
    console.log("Call service createPhoneCall with options:", options)
    const requestData = {
      agent_id: agentId,
      from_number: fromNumber,
      to_number: toNumber,
      ...options,
    }
    console.log("Request data being sent to API:", requestData)
    const response = await api.post<CreatePhoneCallResponse>("/calls/phone", requestData)
    return response.data
  }

  /** Register a call for custom telephony; returns `call_id` and SIP details per Retell. */
  registerPhoneCall = async (body: RegisterPhoneCallParams) => {
    const response = await api.post<Record<string, unknown>>("/calls/register-phone", body)
    return response.data
  }
}

export const callService = new CallService()
